/**
 * Custom-column cell for `team` columns. Click to edit, save on blur/Enter,
 * cancel on Escape; an empty value clears the cell.
 *
 * The value is the team's name, not a reference: the platform has no team or
 * squad entity to point at, so there is nothing to resolve a stored id
 * against. When one exists this cell becomes a picker and the stored names
 * migrate to ids.
 */
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useInlineCellEdit } from '../../lib/useInlineCellEdit';

interface TeamCustomCellProps {
  value: string | undefined;
  onChange: (next: string | null) => void;
}

export function TeamCustomCell({ value, onChange }: TeamCustomCellProps) {
  const { t } = useTranslation('trackers');

  const { editing, inputProps, triggerProps } = useInlineCellEdit(value ?? '', (draft) => {
    if (draft === (value ?? '')) return;
    onChange(draft === '' ? null : draft);
  });

  if (editing) {
    return (
      <input
        {...inputProps}
        placeholder={t('trackersBoard.teamCustomCell.teamName')}
        className="w-full bg-transparent text-center text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    );
  }

  return (
    <button
      {...triggerProps}
      className="flex w-full items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {value ? (
        <span className="inline-flex max-w-full items-center gap-1 truncate rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
          <Building2 className="size-3 shrink-0" aria-hidden />
          <span className="truncate">{value}</span>
        </span>
      ) : (
        <Building2
          className="size-4 text-muted-foreground"
          aria-label={t('trackersBoard.teamCustomCell.setTeam')}
        />
      )}
    </button>
  );
}
